<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router";
import { computed, Ref, ref } from "vue";
import { useGlobalUser } from "@/stores/globalUser";
import api from "@/modules/api";
import { useAppUpdate } from "@/stores/appUpdate";
import { clearCache, copyToClipboard } from "@/modules/common";
import { useI18n } from "vue-i18n";
import { useGlobalModal } from "@/stores/globalModal";
import { showConfirmDialog, showSuccessToast } from "vant";
const route = useRoute();
const router = useRouter();
const user = useGlobalUser();
const appUpdateStore = useAppUpdate();
const { t } = useI18n();
const modalStore = useGlobalModal();
const showAndroidVersionUpdate = ref(false);
const settingMenuList: {
  label: string;
  icon: string;
  route: string;
  value?: string;
  hide?: Ref<boolean>;
  callback?: () => void;
}[] = [
  {
    label: "setting.preference",
    icon: "mingcute--settings-6-line",
    route: "preferenceSetting",
    hide: computed(() => !user.isLogin || user.isCN),
  },
  {
    icon: "mingcute--user-4-line",
    label: "setting.accountInfo",
    route: "accountInfo",
  },
  {
    icon: "mingcute--content-ai-line",
    label: "setting.content",
    route: "contentSetting",
  },
  {
    icon: "solar--cpu-bold",
    label: "setting.currentModel",
    route: "currentModel",
    value: "model",
    callback: () => {
      modalStore.showSelectModelModal = true;
    },
  },
];

const operationList: {
  label: string;
  icon: string;
  callback: () => void;
  hide?: Ref<boolean>;
}[] = [
  {
    label: "setting.about",
    icon: "mingcute--information-line",
    callback: () => {
      router.push({ name: "about" });
    },
  },
  {
    label: "SidebarNavi.checkUpdate",
    icon: "mingcute--align-arrow-up-line",
    callback: () => {
      appUpdateStore.checkUpdate();
    },
  },
  {
    label: "SidebarNavi.clearCache",
    icon: "mingcute--delete-2-line",
    callback: () => {
      clearCache();
    },
  },
  {
    icon: "iconify mingcute--report-line",
    label: t("Cell.report"),
    callback: () => {
      contactCustomerService();
    },
  },
];

const contactCustomerService = async () => {
  try {
    await showConfirmDialog({
      title: t("Message.contactCustomerService"),
      message: t("Message.contactCustomerServiceDesc"),
      confirmButtonText: t("Buttons.copyGroupNumber"),
      cancelButtonText: t("Buttons.cancel"),
    });
    copyToClipboard("795055136");
    showSuccessToast({
      message: t("Message.copySuccessful"),
      duration: 500,
    });
  } catch (e) {
    console.log(e);
  }
};
const isRootPath = computed(() => route.name === "setting");
const logout = async () => {
  await api.signOut();
  user.logout();
  // await router.replace(HOME_ROUTER);
};

const isSuperUser = computed(
  () =>
    [1, 2, 3, 4, 5].includes(user.user.id) ||
    import.meta.env.MODE === "development"
);

const clickOpenLog = () => {
  import("vconsole").then(({ default: VConsole }) => {
    new VConsole();
  });
};
</script>
<template>
  <div class="h-full flex overflow-hidden">
    <div
      :class="isRootPath ? 'block lg:flex-none flex-1' : 'hidden lg:block'"
      class="border-e sm:max-w-[600px]"
    >
      <div class="min-h-full w-full flex flex-col">
        <ThreadsNav :show-center="false" show-back />
        <div class="flex flex-col justify-between flex-grow">
          <div class="flex flex-col gap-4">
            <div class="bg-base-200 mx-4 rounded-lg overflow-hidden">
              <template v-for="item in settingMenuList" :key="item.route">
                <CellItem
                  v-if="!item.value"
                  v-show="!item.hide?.value"
                  :icon="`iconify ${item.icon}`"
                  :label="$t(item.label)"
                  is-link
                  :value="item.value"
                  @click="router.push({ name: item.route })"
                />

                <CellItem
                  v-else-if="item.value === 'model'"
                  :icon="`iconify ${item.icon}`"
                  :label="$t(item.label)"
                  :value="item.value"
                  @click="item.callback?.()"
                  :key="user.user.preferredModel"
                >
                  <template #default>
                    <span class="flex items-center gap-1">
                      <span class="badge badge-outline">
                        {{ t(`chat.model.${user.user.preferredModel}.name`) }}
                      </span>
                      <i
                        class="iconify mingcute--right-line text-base-content/50"
                      />
                    </span>
                  </template>
                </CellItem>
              </template>
            </div>
            <div class="bg-base-200 mx-4 rounded-lg overflow-hidden">
              <CellItem
                v-for="(item, index) in operationList"
                :key="index"
                :icon="`iconify ${item.icon}`"
                :label="$t(item.label)"
                @click="item.callback()"
              />

              <CellItem
                v-if="isSuperUser"
                icon="iconify mingcute--android-2-fill"
                label="更新APK"
                @click="showAndroidVersionUpdate = true"
              />

              <CellItem
                v-if="isSuperUser"
                icon="iconify mingcute--bug-line"
                label="打开日志"
                @click="clickOpenLog"
              />
            </div>
          </div>
          <div v-if="user.isLogin" class="w-full p-4" @click="logout">
            <button class="btn btn-ghost w-full text-error">
              {{ $t("SidebarNavi.logout") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalAndroidVersionUpdate v-model:show="showAndroidVersionUpdate" />

    <div :class="isRootPath ? 'hidden lg:block' : 'block'" class="flex-1">
      <router-view />
    </div>
  </div>
</template>
